import { useMemo, useState } from 'react';
import { api, ifMatchFor } from '../api/client';
import { qk, useGeoEvents, useOccupancyHistory, useOccupancySummary, usePresence, useSources } from '../api/queries';
import { useAction } from '../components/feedback';
import { Async, Badge, Card, Field, PageHeader, Segmented } from '../components/ui';
import { byId } from '../lib/domain';
import { dayTime, duration, nowIso } from '../lib/time';

export function PresencePage() {
  const presence = usePresence();
  return (
    <>
      <PageHeader
        title="Présence & occupation"
        subtitle="Le logement est occupé tant qu’au moins une source est à l’intérieur."
        actions={presence.data && <Badge tone={presence.data.present ? 'ok' : 'neutral'}>{presence.data.present ? 'Présent' : 'Absent'}{presence.data.since ? ` depuis ${dayTime(presence.data.since)}` : ''}</Badge>}
      />
      <div className="split">
        <div className="split-side"><Sources /></div>
        <div className="split-main stack-l">
          <Summary />
          <div className="col-grid">
            <Intervals />
            <Events />
          </div>
        </div>
      </div>
    </>
  );
}

function Sources() {
  const { run, busy } = useAction();
  const sources = useSources();
  const [name, setName] = useState('');
  const inv = [qk.sources, qk.presence, ['geofencing'], qk.state, ['occupancy']];
  const signal = (id: string, kind: 'enter' | 'leave') =>
    run(() => api.send('POST', `/geofencing/sources/${id}/${kind}`, { body: { occurred_at: nowIso() } }), {
      success: kind === 'enter' ? 'Entrée signalée' : 'Sortie signalée', invalidate: inv,
    });

  return (
    <Card title="Sources de géofencing">
      <div className="stack">
        <Async q={{ data: sources.data, isLoading: sources.isLoading, error: sources.error }} empty={<p className="small muted">Aucune source : le logement est toujours considéré présent.</p>}>
          {(list) => list.map((s) => (
            <div key={s.id} className="panel stack-s">
              <div className="row between gap-s wrap">
                <div><div className="strong">{s.name}</div><div className="xsmall muted">changé {dayTime(s.changed_at)}</div></div>
                <Badge tone={s.present ? 'ok' : 'neutral'}>{s.present ? 'À l’intérieur' : 'Sorti'}</Badge>
              </div>
              <div className="row gap-s wrap">
                <button type="button" className="btn btn-s" disabled={busy} onClick={() => signal(s.id, 'enter')}>Signaler une entrée</button>
                <button type="button" className="btn btn-s" disabled={busy} onClick={() => signal(s.id, 'leave')}>Signaler une sortie</button>
                <button type="button" className="btn btn-s btn-danger" disabled={busy}
                  onClick={() => {
                    if (!window.confirm(`Supprimer la source « ${s.name} » ? La présence sera recalculée.`)) return;
                    run(() => api.send('DELETE', `/geofencing/sources/${s.id}`, { ifMatch: ifMatchFor(`/geofencing/sources/${s.id}`, s.version) }), { success: 'Source supprimée', invalidate: inv });
                  }}>
                  Supprimer
                </button>
              </div>
            </div>
          ))}
        </Async>
        <form className="row gap-s wrap align-end" onSubmit={(e) => {
          e.preventDefault();
          run(() => api.send('POST', '/geofencing/sources', { body: { name: name.trim(), present: true } }), { success: 'Source ajoutée (présente)', invalidate: inv }).then((r) => r && setName(''));
        }}>
          <Field label="Nouvelle source" htmlFor="g-name"><input id="g-name" placeholder="ex. iPhone Michael" value={name} onChange={(e) => setName(e.target.value)} /></Field>
          <button className="btn" disabled={busy || !name.trim()}>Ajouter</button>
        </form>
        <p className="xsmall muted">Les boutons servent aux essais. En usage normal, l’automatisation du téléphone appelle /geo avec son propre jeton limité et l’heure réelle de l’événement.</p>
      </div>
    </Card>
  );
}

type Row = Record<string, unknown>;
/** Le schéma OccupancySummary.data est un objet libre (REQ-39) : lecture tolérante des champs usuels. */
function summaryRows(data: Row[]) {
  return data.map((r) => {
    const label = [r.period, r.date, r.day, r.week, r.month, r.key, r.label, r.start].find((v) => typeof v === 'string') as string | undefined
      ?? Object.values(r).find((v) => typeof v === 'string') as string | undefined ?? '';
    let minutes = [r.present_minutes, r.minutes, r.duration_minutes].find((v) => typeof v === 'number') as number | undefined;
    if (minutes === undefined) {
      const sec = [r.present_seconds, r.seconds, r.duration_seconds].find((v) => typeof v === 'number') as number | undefined;
      if (sec !== undefined) minutes = sec / 60;
    }
    if (minutes === undefined) {
      const hours = [r.present_hours, r.hours].find((v) => typeof v === 'number') as number | undefined;
      if (hours !== undefined) minutes = hours * 60;
    }
    return { label, minutes: minutes ?? 0 };
  });
}

function Summary() {
  const [groupBy, setGroupBy] = useState<'day' | 'week' | 'month'>('day');
  const span = { day: 7, week: 35, month: 150 }[groupBy];
  // Arrondi à la minute près une fois par regroupement : une clé de requête stable évite les rechargements en boucle.
  const from = useMemo(() => new Date(Math.floor(Date.now() / 60_000) * 60_000 - span * 86_400_000).toISOString(), [span]);
  const q = useOccupancySummary(groupBy, from);
  const rows = summaryRows((q.data?.data ?? []) as Row[]);
  const max = Math.max(1, ...rows.map((r) => r.minutes));
  return (
    <Card title="Temps passé au domicile" actions={<Segmented label="Regroupement" value={groupBy} onChange={setGroupBy} options={[{ value: 'day', label: 'Jour' }, { value: 'week', label: 'Semaine' }, { value: 'month', label: 'Mois' }]} />}>
      <Async q={{ data: q.data ? rows : undefined, isLoading: q.isLoading, error: q.error }} empty={<p className="small muted">Pas encore de données d’occupation.</p>}>
        {(rs) => (
          <div className="bars" role="img" aria-label={rs.map((r) => `${r.label} ${duration(r.minutes)}`).join(', ')}>
            {rs.map((r, i) => (
              <div key={i} className="bar-col">
                <span className="mono xsmall">{duration(r.minutes)}</span>
                <span className="bar" style={{ height: `${(r.minutes / max) * 100}%` }} />
                <span className="xsmall muted">{r.label}</span>
              </div>
            ))}
          </div>
        )}
      </Async>
      <p className="xsmall muted">Les périodes sans observation (service arrêté) sont exclues.</p>
    </Card>
  );
}

function Intervals() {
  const q = useOccupancyHistory();
  return (
    <Card title="Intervalles de présence">
      <Async q={{ data: q.data?.data, isLoading: q.isLoading, error: q.error }} empty={<p className="small muted">Aucun intervalle.</p>}>
        {(rows) => (
          <ul className="timeline-list">
            {rows.map((r) => (
              <li key={r.id}>
                <span className="mono">{dayTime(r.started_at)} → {r.ended_at ? dayTime(r.ended_at) : 'en cours'}</span>
                <span className="small muted">{r.ended_at ? duration((new Date(r.ended_at).getTime() - new Date(r.started_at).getTime()) / 60000) : ''}</span>
              </li>
            ))}
          </ul>
        )}
      </Async>
    </Card>
  );
}

function Events() {
  const q = useGeoEvents(1);
  const sources = useSources();
  const map = byId(sources.data);
  return (
    <Card title="Journal des entrées / sorties">
      <Async q={{ data: q.data?.data, isLoading: q.isLoading, error: q.error }} empty={<p className="small muted">Aucun événement.</p>}>
        {(rows) => (
          <ul className="timeline-list">
            {rows.map((e, i) => {
              const entered = e.present ?? (e.state ? /enter|entr/i.test(e.state) : undefined);
              return (
                <li key={e.id ?? i}>
                  <span className="mono muted">{dayTime(e.occurred_at)}</span>
                  <span className="small">
                    {map.get(e.source_id)?.name ?? e.source_id} · {entered === undefined ? e.state ?? '—' : entered ? 'entrée' : 'sortie'}
                    {e.applied === false && <span className="block text-warn">ignoré · reçu en retard</span>}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </Async>
    </Card>
  );
}
